import { useEffect, useMemo, useState, useCallback } from "react";

import { Typography } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { DEFAULT_REQUEST, DEFAULT_REQUEST_REMARK } from "./constants";
import {
  canSaveOutsideEnrollmentAreaRequest,
  generateSecondaryInstitutionApprovedText,
  getVisibilityState,
} from "./utils";
import { handleStatusTransition } from "./utils/status-handlers";
import { getInstitutionById } from "../../api/Institution/InstitutionApi";
import {
  getEnrollmentDecision,
  getRequest,
  setOutsideEnrollmentAreaStatus,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import { Role, ValidationFailure } from "../../models";
import {
  EnrollmentAreaRadioGroups,
  RequestRemarkTextArea,
} from "./OutsideEnrollment/components";
import { getRemarkOrDefault } from "./utils/remarks";
import { Institution } from "../../models/Institution";
import {
  Request,
  OutsideEnrollmentAreaStatus,
  InstitutionChangeReason,
  EnrollmentDecision,
  RequestRemark,
  SetOutsideEnrollmentAreaStatusPayload,
} from "../../models/Request";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import { setIsEdited, resetIsEdited } from "../../slices/applicationSlice";
import { isStatusSuggestedEnrollment } from "../../utils/status.util";

export function OutsideEnrollmentAreaEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const { requestStatusCodeBook, institutionChangeReasonCodeBook } =
    useEnumCodeBooks();

  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [enrollmentDecision, setEnrollmentDecision] =
    useState<EnrollmentDecision>();
  const [isApproved, setIsApproved] = useState(false);
  const [nextStatus, setNextStatus] = useState<OutsideEnrollmentAreaStatus>(
    OutsideEnrollmentAreaStatus.NotProcessed
  );
  const [primaryInstitution, setPrimaryInstitution] = useState<Institution>();
  const [secondaryInstitution, setSecondaryInstitution] =
    useState<Institution>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [isEditing, setIsEditing] = useState(false);
  const [requestRemark, setRequestRemark] = useState<RequestRemark>(
    DEFAULT_REQUEST_REMARK
  );
  const [hasFinalDecision, setHasFinalDecision] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const checkCanSaveRequest = useCallback(
    (requestData: Request, enrollmentDecisionData?: EnrollmentDecision) =>
      canSaveOutsideEnrollmentAreaRequest(
        requestData,
        auth,
        schoolYear?.id,
        enrollmentDecisionData
      ),
    [auth, schoolYear]
  );

  const canSaveRequest = useMemo(
    () => checkCanSaveRequest(item, enrollmentDecision),
    [checkCanSaveRequest, item, enrollmentDecision]
  );

  const fetchData = useCallback(async () => {
    if (!id || id === "0" || !schoolYear) return;

    const result = await getRequest(+id);
    if (!result) return;

    setItem(result);

    const currentRemark = getRemarkOrDefault({
      remarks: result.remarks,
      remarkTypeId: 1,
      requestId: result.id,
    });
    setRequestRemark(currentRemark);

    const enrollmentDecision = await getEnrollmentDecision(+id);
    setEnrollmentDecision(enrollmentDecision);

    const canSave = checkCanSaveRequest(result, enrollmentDecision);
    if (!canSave) return;

    const currentRole = auth.currentRole?.role;
    const {
      nextStatus,
      isApproved,
      shouldEdit,
      shouldSetEdited,
      hasFinalDecision,
      canEdit,
    } = handleStatusTransition(result, currentRole);

    setNextStatus(nextStatus);
    setIsApproved(isApproved);
    setIsEditing(shouldEdit);
    setHasFinalDecision(hasFinalDecision);
    setCanEdit(canEdit);

    if (shouldSetEdited) {
      dispatch(setIsEdited());
    }
  }, [id, schoolYear, checkCanSaveRequest, auth.currentRole?.role, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    (async () => {
      if (
        item.institutionId &&
        item.schoolYearId > 0 &&
        (!primaryInstitution || primaryInstitution.id !== item.institutionId)
      ) {
        setPrimaryInstitution(
          await getInstitutionById(item.institutionId, item.schoolYearId)
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);

  useEffect(() => {
    (async () => {
      if (
        item.outsideEnrollmentAreaInstitutionProgram?.institutionId &&
        item.schoolYearId > 0 &&
        (!secondaryInstitution ||
          secondaryInstitution.id !==
            item.outsideEnrollmentAreaInstitutionProgram?.institutionId)
      ) {
        setSecondaryInstitution(
          await getInstitutionById(
            item.outsideEnrollmentAreaInstitutionProgram?.institutionId,
            item.schoolYearId
          )
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    item.outsideEnrollmentAreaInstitutionProgram?.institutionId,
    item.schoolYearId,
  ]);

  const handleIsApprovedChange = (val: boolean) => {
    if (hasFinalDecision) {
      setNextStatus(
        val
          ? OutsideEnrollmentAreaStatus.DecisionAccepted
          : OutsideEnrollmentAreaStatus.DecisionDeclined
      );
    } else if (isStatusSuggestedEnrollment(item.outsideEnrollmentAreaStatus)) {
      // When the user changes checkboxes, set the matching suggested decision status.
      setNextStatus(
        val
          ? OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted
          : OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected
      );
    }

    setIsApproved(val);
    dispatch(setIsEdited());
  };

  const editButtonVisibility = getVisibilityState(
    isEditing,
    canSaveRequest,
    canEdit
  );

  const handleSaveSuccess = useCallback(() => {
    dispatch(resetIsEdited());
    setIsEditing(false);
    fetchData();
  }, [dispatch, fetchData]);

  const handleNoteChange = (updatedRemark: RequestRemark) => {
    setRequestRemark(updatedRemark);
    dispatch(setIsEdited());
  };

  const handleSave = async () => {
    const payload: SetOutsideEnrollmentAreaStatusPayload = {
      requestId: item.id,
      outsideEnrollmentAreaStatus: nextStatus,
      isApproved,
      requestRemark: requestRemark,
    };

    return await setOutsideEnrollmentAreaStatus(payload);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={editButtonVisibility}
        onClickEdit={() => setIsEditing(true)}
        pageTitle={t("suggestedOutsideEnrollmentArea.pageTitle")}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        <Row>
          <Col md={12}>
            <Typography component="span">
              {t("suggestedOutsideEnrollmentArea.primaryInstitution")}:&nbsp;
              <b>{primaryInstitution?.name}</b>
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography>
              {t("suggestedOutsideEnrollmentArea.secondaryInstitution")}:&nbsp;
              <b>{secondaryInstitution?.name}</b>
            </Typography>
          </Col>
        </Row>
        <Row />
        <Row>
          <Col md={12}>
            <Typography>
              {t("suggestedOutsideEnrollmentArea.status")}:&nbsp;
              <b>
                {
                  requestStatusCodeBook.find((v) => v.id === item.statusId)
                    ?.name
                }
              </b>
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography>
              {t("suggestedOutsideEnrollmentArea.reason")}:&nbsp;
              <b>
                {
                  institutionChangeReasonCodeBook.find(
                    (v) => v.id === item.institutionChangeReason
                  )?.name
                }
              </b>
            </Typography>
          </Col>
        </Row>
        {item.institutionChangeReason === InstitutionChangeReason.Other &&
          item.institutionChangeReasonOther && (
            <Row>
              <Col md={12}>
                <Typography>
                  {t("suggestedOutsideEnrollmentArea.reasonOther")}:&nbsp;
                  <b>{item.institutionChangeReasonOther}</b>
                </Typography>
              </Col>
            </Row>
          )}
        {item.outsideEnrollmentAreaStatus !==
          OutsideEnrollmentAreaStatus.NotProcessed && (
          <Row>
            <Col md={12}>
              <Typography>
                {t(
                  "suggestedOutsideEnrollmentArea.secondaryInstitutionApproved"
                )}
                :&nbsp;
                <b>
                  {generateSecondaryInstitutionApprovedText(
                    item,
                    enrollmentDecision
                  )}
                </b>
              </Typography>
            </Col>
          </Row>
        )}

        <EnrollmentAreaRadioGroups
          isEditing={isEditing}
          isApproved={isApproved}
          onApprovalChange={handleIsApprovedChange}
          enrollmentDecision={enrollmentDecision}
          primaryInstitution={primaryInstitution}
          secondaryInstitution={secondaryInstitution}
          request={item}
          hasFinalDecision={hasFinalDecision}
        />
      </FormCard>

      <FormCard>
        <RequestRemarkTextArea
          requestRemark={requestRemark}
          isEditing={isEditing}
          outsideEnrollmentAreaStatus={item.outsideEnrollmentAreaStatus}
          onChange={handleNoteChange}
        />
      </FormCard>

      <ToolbarEdit
        showCancel={"show"}
        showSave={canSaveRequest && isEditing ? "show" : "disable"}
        onSaveSuccess={handleSaveSuccess}
        handleSave={handleSave}
        setSaveErrors={setErrors}
      />
    </ContentArea>
  );
}
